import React, { Component } from 'react';
import axios from 'axios';
import moment from 'moment';
import Chart from 'chart.js';
import '../tools/css/Consultas_programada.css';
import '../tools/css/Anexos.css';

const ApiURL = process.env.REACT_APP_API_URL;

// Paleta categórica validada (skill de dataviz) para identificar cada "tipo de anexo" con el mismo color en todas las gráficas del informe.
const PALETA_CATEGORICA = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const COLOR_MARCA = '#AD1457';
const COLOR_OTROS = '#c3c2b7';
const TEXTO_MUTED = '#898781';
const TEXTO_SECUNDARIO = '#52514e';
const GRID_COLOR = '#e1e0d9';
const EJE_COLOR = '#c3c2b7';

const LIMITE_PAGINAS_SEGURIDAD = 500;

/**
 * Informe de anexos (pestaña "Informe" de Anexos.js). A diferencia de
 * InformeConsultas.js, este componente NO recibe la data por prop: como el
 * padre solo tiene cargada la página actual del listado, este informe
 * recorre por su cuenta TODAS las páginas de GetAttachsInstitution la
 * primera vez que se abre la pestaña, y a partir de ahí solo filtra/redibuja
 * en el cliente cuando cambia el rango de fechas (sin volver a pedir nada).
 * El padre dispara dibujarGraficas() por ref al abrir la pestaña, porque los
 * canvas de Chart.js miden 0 si se dibujan dentro de un tab-pane oculto.
 */
class InformeAnexos extends Component {

  chartTipoRef = React.createRef();
  chartProfesionalRef = React.createRef();
  chartFechaRef = React.createRef();

  chartTipo = null;
  chartProfesional = null;
  chartFecha = null;

  // Mapa estable tipo -> color: una vez asignado un color a un tipo de anexo
  // no cambia, aunque el filtro de fechas reduzca los datos.
  colorPorTipo = {};

  state = {
    cargando: false,
    datosCargados: false,
    anexos: [],
    fechaDesdeInforme: moment().startOf('month').format('YYYY-MM-DD'),
    fechaHastaInforme: moment().format('YYYY-MM-DD')
  };

  componentWillUnmount(){
    [this.chartTipo, this.chartProfesional, this.chartFecha].forEach((grafica) => {
      if (grafica) grafica.destroy();
    });
  }

  obtenerTipoAnexo = (a) => (a.typeAttach && a.typeAttach.name) || 'Sin tipo';
  obtenerProfesional = (a) => a.profesionalName || 'Sin profesional';

  asignarColoresTipo = (anexos) => {
    const tiposOrdenados = Array.from(new Set(anexos.map(this.obtenerTipoAnexo))).sort((a, b) => a.localeCompare(b));
    tiposOrdenados.forEach((tipo) => {
      if (!this.colorPorTipo[tipo]) {
        const indice = Object.keys(this.colorPorTipo).length;
        this.colorPorTipo[tipo] = indice < PALETA_CATEGORICA.length ? PALETA_CATEGORICA[indice] : COLOR_OTROS;
      }
    });
  };

  anexosFiltrados = () => {
    const { anexos, fechaDesdeInforme, fechaHastaInforme } = this.state;
    return (anexos || []).filter((a) => {
      if (!a) return false;
      if (fechaDesdeInforme && moment(a.date).isBefore(moment(fechaDesdeInforme, 'YYYY-MM-DD'), 'day')) return false;
      if (fechaHastaInforme && moment(a.date).isAfter(moment(fechaHastaInforme, 'YYYY-MM-DD'), 'day')) return false;
      return true;
    });
  };

  cambiarFechaDesdeInforme = (e) => {
    this.setState({ fechaDesdeInforme: e.target.value }, () => this.dibujarTodasLasGraficas());
  };

  cambiarFechaHastaInforme = (e) => {
    this.setState({ fechaHastaInforme: e.target.value }, () => this.dibujarTodasLasGraficas());
  };

  // Punto de entrada que dispara el padre por ref al abrir la pestaña: la
  // primera vez recorre todas las páginas del endpoint, las siguientes veces
  // solo redibuja con lo que ya está en memoria.
  dibujarGraficas = () => {
    if (!this.state.datosCargados) {
      this.cargarTodosLosAnexos();
    } else {
      this.dibujarTodasLasGraficas();
    }
  };

  recargarInforme = () => {
    this.setState({ datosCargados: false }, () => this.cargarTodosLosAnexos());
  };

  cargarTodosLosAnexos = () => {
    this.setState({ cargando: true });
    this.recolectarAnexos(1, []);
  };

  recolectarAnexos = (pagina, acumulado) => {
    axios.get(ApiURL + "/api/Attach/GetAttachsInstitution?numeroPagina=" + pagina)
    .then((response) => {
      const datos = (response.data && response.data.data) || [];
      const acumuladoNuevo = acumulado.concat(datos);

      if (datos.length > 0 && pagina < LIMITE_PAGINAS_SEGURIDAD) {
        this.recolectarAnexos(pagina + 1, acumuladoNuevo);
      } else {
        this.setState({ anexos: acumuladoNuevo, cargando: false, datosCargados: true }, () => this.dibujarTodasLasGraficas());
      }
    })
    .catch((error) => {
      console.log(error);
      this.setState({ cargando: false });
    });
  };

  dibujarTodasLasGraficas = () => {
    const anexos = this.anexosFiltrados();
    this.asignarColoresTipo(anexos);
    this.dibujarGraficaTipo(anexos);
    this.dibujarGraficaProfesional(anexos);
    this.dibujarGraficaFecha(anexos);
  };

  // Doughnut: identidad = color (no hay eje), por eso lleva leyenda y paleta categórica.
  dibujarGraficaTipo = (anexos) => {
    const lienzo = this.chartTipoRef.current;
    if (!lienzo) return;

    const conteo = {};
    anexos.forEach((a) => {
      const tipo = this.obtenerTipoAnexo(a);
      conteo[tipo] = (conteo[tipo] || 0) + 1;
    });

    const etiquetas = Object.keys(conteo);
    const datos = etiquetas.map((t) => conteo[t]);
    const colores = etiquetas.map((t) => this.colorPorTipo[t] || COLOR_OTROS);

    if (this.chartTipo) this.chartTipo.destroy();

    this.chartTipo = new Chart(lienzo.getContext('2d'), {
      type: 'doughnut',
      data: {
        labels: etiquetas,
        datasets: [{
          data: datos,
          backgroundColor: colores,
          borderColor: '#ffffff',
          borderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 62,
        legend: {
          position: 'bottom',
          labels: { boxWidth: 12, fontColor: TEXTO_SECUNDARIO, fontSize: 12, padding: 14 }
        },
        tooltips: {
          callbacks: {
            label: (item, data) => {
              const etiqueta = data.labels[item.index];
              const valor = data.datasets[0].data[item.index];
              return `${etiqueta}: ${valor} anexo${valor === 1 ? '' : 's'}`;
            }
          }
        }
      }
    });
  };

  // Bar horizontal: ranking de profesionales (top 10) por cantidad de anexos
  // registrados. Un solo color de marca porque es una magnitud, no identidad.
  dibujarGraficaProfesional = (anexos) => {
    const lienzo = this.chartProfesionalRef.current;
    if (!lienzo) return;

    const conteo = {};
    anexos.forEach((a) => {
      const profesional = this.obtenerProfesional(a);
      conteo[profesional] = (conteo[profesional] || 0) + 1;
    });

    const topProfesionales = Object.keys(conteo)
      .map((p) => ({ profesional: p, cantidad: conteo[p] }))
      .sort((a, b) => b.cantidad - a.cantidad)
      .slice(0, 10)
      .reverse(); // horizontalBar dibuja de abajo hacia arriba: así el #1 queda arriba

    const etiquetas = topProfesionales.map((p) => p.profesional);
    const datos = topProfesionales.map((p) => p.cantidad);

    if (this.chartProfesional) this.chartProfesional.destroy();

    this.chartProfesional = new Chart(lienzo.getContext('2d'), {
      type: 'horizontalBar',
      data: {
        labels: etiquetas,
        datasets: [{
          label: 'Anexos',
          data: datos,
          backgroundColor: COLOR_MARCA,
          barPercentage: 0.6,
          categoryPercentage: 0.7
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{
            gridLines: { color: GRID_COLOR, drawBorder: false, zeroLineColor: EJE_COLOR },
            ticks: { beginAtZero: true, fontColor: TEXTO_MUTED, precision: 0 }
          }],
          yAxes: [{
            gridLines: { display: false, drawBorder: true, color: EJE_COLOR },
            ticks: { fontColor: TEXTO_SECUNDARIO, fontSize: 12 }
          }]
        },
        tooltips: {
          callbacks: {
            label: (item) => `${item.xLabel} anexo${item.xLabel === 1 ? '' : 's'}`
          }
        }
      }
    });
  };

  // Line: evolución cronológica (un anexo = un punto en el tiempo), color de marca.
  dibujarGraficaFecha = (anexos) => {
    const lienzo = this.chartFechaRef.current;
    if (!lienzo) return;

    const conteoPorDia = {};
    anexos.forEach((a) => {
      if (!a.date) return;
      const clave = moment(a.date).format('YYYY-MM-DD');
      conteoPorDia[clave] = (conteoPorDia[clave] || 0) + 1;
    });

    const clavesOrdenadas = Object.keys(conteoPorDia).sort();
    const etiquetas = clavesOrdenadas.map((clave) => moment(clave).format('DD/MM'));
    const datos = clavesOrdenadas.map((clave) => conteoPorDia[clave]);

    if (this.chartFecha) this.chartFecha.destroy();

    this.chartFecha = new Chart(lienzo.getContext('2d'), {
      type: 'line',
      data: {
        labels: etiquetas,
        datasets: [{
          label: 'Anexos',
          data: datos,
          borderColor: COLOR_MARCA,
          backgroundColor: 'rgba(173, 20, 87, .12)',
          pointBackgroundColor: COLOR_MARCA,
          pointRadius: 3,
          fill: true,
          lineTension: 0.25
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{
            gridLines: { display: false, drawBorder: true, color: EJE_COLOR },
            ticks: { fontColor: TEXTO_MUTED, fontSize: 11, maxRotation: 0 }
          }],
          yAxes: [{
            gridLines: { color: GRID_COLOR, drawBorder: false, zeroLineColor: EJE_COLOR },
            ticks: { beginAtZero: true, fontColor: TEXTO_MUTED, precision: 0 }
          }]
        },
        tooltips: {
          callbacks: {
            label: (item) => `${item.yLabel} anexo${item.yLabel === 1 ? '' : 's'}`
          }
        }
      }
    });
  };

  render(){
    const anexos = this.anexosFiltrados();
    const total = anexos.length;
    const pacientesUnicos = new Set(anexos.map((a) => a.patientName).filter(Boolean)).size;
    const profesionalesUnicos = new Set(anexos.map(this.obtenerProfesional)).size;

    const resumenPorTipo = {};
    anexos.forEach((a) => {
      const tipo = this.obtenerTipoAnexo(a);
      resumenPorTipo[tipo] = (resumenPorTipo[tipo] || 0) + 1;
    });

    const tipoMasFrecuente = Object.keys(resumenPorTipo).sort((a, b) => resumenPorTipo[b] - resumenPorTipo[a])[0] || 'N/A';

    return (
      <div className="informe-anexos">

        <div className="row consultas-toolbar">
          <div className="col-sm-3">
            <div className="input-group input-group-sm mb-3">
              <div className="input-group-prepend">
                <div className="input-group-text">Desde</div>
              </div>
              <input type="date" className="form-control" value={this.state.fechaDesdeInforme} onChange={this.cambiarFechaDesdeInforme}/>
            </div>
          </div>
          <div className="col-sm-3">
            <div className="input-group input-group-sm mb-3">
              <div className="input-group-prepend">
                <div className="input-group-text">Hasta</div>
              </div>
              <input type="date" className="form-control" value={this.state.fechaHastaInforme} onChange={this.cambiarFechaHastaInforme}/>
            </div>
          </div>
          <div className="col-sm-6 anexos-informe-toolbar__acciones">
            <button type="button" className="btn anexos-btn-actualizar" disabled={this.state.cargando} onClick={()=> this.recargarInforme()}>
              {this.state.cargando ? <i className="fas fa-circle-notch fa-spin"></i> : <i className="fas fa-sync-alt"></i>}
              {this.state.cargando ? ' Cargando...' : ' Actualizar informe'}
            </button>
          </div>
        </div>

        {this.state.cargando ? (

          <div className="d-flex justify-content-center" style={{padding:'40px 0'}}>
            <div className="spinner-border" style={{color:'#AD1457'}} role="status"></div>
          </div>

        ) : (

          <React.Fragment>

            <div className="row consultas-kpis">
              <div className="col-md-3">
                <div className="consultas-stat">
                  <span className="consultas-stat-label">Total anexos</span>
                  <span className="consultas-stat-value">{total.toLocaleString('es-CO')}</span>
                </div>
              </div>
              <div className="col-md-3">
                <div className="consultas-stat">
                  <span className="consultas-stat-label">Pacientes con anexos</span>
                  <span className="consultas-stat-value">{pacientesUnicos.toLocaleString('es-CO')}</span>
                </div>
              </div>
              <div className="col-md-3">
                <div className="consultas-stat">
                  <span className="consultas-stat-label">Profesionales que anexaron</span>
                  <span className="consultas-stat-value">{profesionalesUnicos.toLocaleString('es-CO')}</span>
                </div>
              </div>
              <div className="col-md-3">
                <div className="consultas-stat">
                  <span className="consultas-stat-label">Tipo más frecuente</span>
                  <span className="consultas-stat-value anexos-stat-value--pequeno">{tipoMasFrecuente}</span>
                </div>
              </div>
            </div>

            <div className="row consultas-graficas">

              <div className="col-md-5">
                <div className="card">
                  <div className="card-header"><h3 className="card-title">Anexos por tipo</h3></div>
                  <div className="card-body">
                    <div className="consultas-canvas-wrap">
                      <canvas ref={this.chartTipoRef}></canvas>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-7">
                <div className="card">
                  <div className="card-header"><h3 className="card-title">Anexos por profesional (top 10)</h3></div>
                  <div className="card-body">
                    <div className="consultas-canvas-wrap">
                      <canvas ref={this.chartProfesionalRef}></canvas>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-12">
                <div className="card">
                  <div className="card-header"><h3 className="card-title">Evolución de anexos por fecha</h3></div>
                  <div className="card-body">
                    <div className="consultas-canvas-wrap consultas-canvas-wrap-lg">
                      <canvas ref={this.chartFechaRef}></canvas>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            <div className="row">
              <div className="col-md-12">
                <div className="card table-responsive">
                  <div className="card-header"><h3 className="card-title">Resumen por tipo de anexo</h3></div>
                  <div className="card-body">
                    <div className="consultas-table-wrap">
                      <table className="table table-bordered table-striped table-hover consultas-table">
                        <thead>
                          <tr><th>Tipo de anexo</th><th>Cantidad</th><th>% del total</th></tr>
                        </thead>
                        <tbody>
                          {Object.keys(resumenPorTipo).sort((a, b) => resumenPorTipo[b] - resumenPorTipo[a]).map((tipo) => (
                            <tr key={tipo}>
                              <td>{tipo}</td>
                              <td>{resumenPorTipo[tipo]}</td>
                              <td>{total ? ((resumenPorTipo[tipo] / total) * 100).toFixed(1) : '0.0'}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <p className="consultas-nota">Este informe recorre todas las páginas de anexos disponibles en el rango de fechas elegido, no solo la página cargada en la pestaña "Anexos". Usa "Actualizar informe" para volver a traer los datos más recientes.</p>

          </React.Fragment>

        )}

      </div>
    );
  }
}

export default InformeAnexos;
